import React from 'react';
import produce from 'immer';
import { useSelector } from 'react-redux';
import CardBody from 'components/Card/CardBody';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { useTranslation } from 'react-i18next';
import { Divider } from '@material-ui/core';
import makeStyles from '@material-ui/styles/makeStyles';
import isEmpty from 'lodash/isEmpty';
import CardWrapper from 'components/Card/CardWrapper';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import { Switch } from 'components/Form/Inputs';

import { SelectInput } from '../../components';
import InputsIndex from './InputsIndex';

const useStyles = makeStyles({
  toggle: {
    display: 'flex',
    marginLeft: 0,
  },
  divider: {
    marginBottom: '20px',
  },
  mergeMode: {
    display: 'flex',
    alignItems: 'center',
  },
  limitLabel: {
    marginTop: '10px',
    marginBottom: 0,
  },
});

const ShopMeals = ({ classes, shopModule, setShopModule }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const currency = useSelector(
    state => state.Brands.brand.multinational.currency
  );

  return (
    <>
      <CardWrapper title={t('$*shop.shopMeals.dishAsOverDish')}>
        <CardBody>
          <Divider className={styles.divider} />
          <Switch
            className={styles.toggle}
            value={Boolean(shopModule?.allowDishesFromPlaning)}
            onChange={value => {
              setShopModule(
                produce(draft => {
                  draft.allowDishesFromPlaning = value;
                })
              );
            }}
            label={
              <InfoIconTooltip
                title={t('$*shop.shopMeals.dishesFromPlanning.tooltip')}
              >
                {t('$*shop.shopMeals.offerDishesFromVariableOffer')}
              </InfoIconTooltip>
            }
          />
          {shopModule?.allowDishesFromPlaning && (
            <div className={styles.mergeMode}>
              <SelectInput
                classes={classes}
                label={t('$*shop.shopMeals.dishesFromPlaningMergeMode')}
                options={[
                  {
                    val: 'NO_MERGE',
                    name: t(
                      '$*shop.shopMeals.dishesFromPlaningMergeMode.NO_MERGE'
                    ),
                  },
                  {
                    val: 'LOWER_COST',
                    name: t(
                      '$*shop.shopMeals.dishesFromPlaningMergeMode.LOWER_COST'
                    ),
                  },
                  {
                    val: 'HIGHER_COST',
                    name: t(
                      '$*shop.shopMeals.dishesFromPlaningMergeMode.HIGHER_COST'
                    ),
                  },
                ]}
                value={shopModule?.dishesFromPlaningMergeMode}
                trackBy="val"
                mapBy="name"
                handleChange={({ target: { value } }) => {
                  setShopModule(
                    produce(draft => {
                      draft.dishesFromPlaningMergeMode = value;
                    })
                  );
                }}
                labelSm={3}
                selectSm={4}
              />
            </div>
          )}
          <Switch
            className={styles.toggle}
            value={Boolean(shopModule?.allowDishesFromOffer)}
            onChange={value => {
              setShopModule(
                produce(draft => {
                  draft.allowDishesFromOffer = value;
                })
              );
            }}
            label={
              <InfoIconTooltip
                title={t('$*shop.shopMeals.dishesFromOffer.tooltip')}
              >
                {t('$*shop.shopMeals.offerDishesFromFixedOffer')}
              </InfoIconTooltip>
            }
          />
        </CardBody>
      </CardWrapper>
      <CardWrapper title={t('$*shop.shopMeals.limitsTitle')}>
        <CardBody>
          <Divider className={styles.divider} />
          <GridContainer>
            <GridItem xs={12}>
              <h4 className={styles.limitLabel}>
                {t('$*shop.shopMeals.rangeDishOrderPrice', { currency })}
              </h4>
            </GridItem>
            <GridItem xs={12} md={3}>
              <InputsIndex
                type="number"
                label={t('$*common.from')}
                value={shopModule?.minimumDailyOrderCost}
                handler={e => {
                  const { value } = e.target;
                  setShopModule(
                    produce(draft => {
                      draft.minimumDailyOrderCost = isEmpty(value)
                        ? null
                        : value;
                    })
                  );
                }}
              />
            </GridItem>
            <GridItem xs={12} md={3}>
              <InputsIndex
                type="number"
                label={t('$*common.to')}
                value={shopModule?.maximumDailyOrderCost}
                handler={e => {
                  const { value } = e.target;
                  setShopModule(
                    produce(draft => {
                      draft.maximumDailyOrderCost = isEmpty(value)
                        ? null
                        : value;
                    })
                  );
                }}
              />
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12}>
              <h4 className={styles.limitLabel}>
                <InfoIconTooltip
                  title={t('$*shop.shopMeals.rangeDishAmount.tooltip')}
                >
                  {t('$*shop.shopMeals.rangeDishAmount')}
                </InfoIconTooltip>
              </h4>
            </GridItem>
            <GridItem xs={12} md={3}>
              <InputsIndex
                type="number"
                label={t('$*common.from')}
                value={shopModule?.minimumDailyDishes}
                handler={e => {
                  const { value } = e.target;
                  setShopModule(
                    produce(draft => {
                      draft.minimumDailyDishes = isEmpty(value) ? null : value;
                    })
                  );
                }}
              />
            </GridItem>
            <GridItem xs={12} md={3}>
              <InputsIndex
                type="number"
                label={t('$*common.to')}
                value={shopModule?.maximumDailyDishes}
                handler={e => {
                  const { value } = e.target;
                  setShopModule(
                    produce(draft => {
                      draft.maximumDailyDishes = isEmpty(value) ? null : value;
                    })
                  );
                }}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>
    </>
  );
};

export default ShopMeals;
